import { useState, useCallback } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@loader/ui';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import { useTestHttpFetch, type TestFetchResult } from '../hooks/useCustomConnectors';
import { SchedulePicker } from './SchedulePicker';
import { DEFAULT_SCHEDULE } from './schedulePickerUtils';

interface HttpFetchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  onSubmit: (data: {
    name: string;
    config: {
      url: string;
      method: 'GET' | 'POST';
      headers?: Record<string, string>;
      body?: string;
      auth_type: 'none' | 'bearer' | 'basic' | 'api_key';
      auth_value?: string;
      auth_header_name?: string;
      response_json_path?: string;
    };
    schedule?: string;
  }) => void;
}

export function HttpFetchDialog({ open, onOpenChange, loading, onSubmit }: HttpFetchDialogProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState<'GET' | 'POST'>('GET');
  const [headersStr, setHeadersStr] = useState('');
  const [body, setBody] = useState('');
  const [authType, setAuthType] = useState<'none' | 'bearer' | 'basic' | 'api_key'>('none');
  const [authValue, setAuthValue] = useState('');
  const [authHeaderName, setAuthHeaderName] = useState('X-API-Key');
  const [jsonPath, setJsonPath] = useState('');
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);

  const [testResult, setTestResult] = useState<TestFetchResult | null>(null);
  const testFetch = useTestHttpFetch();

  // Reset test result when any config field changes
  const clearTest = useCallback(() => setTestResult(null), []);

  const parseHeaders = (): Record<string, string> | undefined => {
    if (!headersStr.trim()) return undefined;
    try {
      const parsed: unknown = JSON.parse(headersStr);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, string>;
      }
    } catch {
      // Fall through to line-by-line parsing
      const headers: Record<string, string> = {};
      for (const line of headersStr.split('\n')) {
        const [key, ...rest] = line.split(':');
        if (key?.trim() && rest.length > 0) {
          headers[key.trim()] = rest.join(':').trim();
        }
      }
      return Object.keys(headers).length > 0 ? headers : undefined;
    }
  };

  const buildConfig = () => {
    const parsedHeaders = parseHeaders();
    return {
      url,
      method,
      ...(parsedHeaders ? { headers: parsedHeaders } : {}),
      ...(method === 'POST' && body ? { body } : {}),
      auth_type: authType,
      ...(authType !== 'none' && authValue ? { auth_value: authValue } : {}),
      ...(authType === 'api_key' ? { auth_header_name: authHeaderName } : {}),
      ...(jsonPath ? { response_json_path: jsonPath } : {}),
    };
  };

  const handleTest = async () => {
    try {
      const result = await testFetch.mutateAsync(buildConfig());
      setTestResult(result);
    } catch {
      setTestResult({ success: false, error: 'Network error — check the URL and try again' });
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    let tableName = name;
    if (!tableName) {
      try { tableName = new URL(url).hostname; } catch { tableName = url; }
    }
    onSubmit({
      name: tableName,
      config: buildConfig(),
      schedule: schedule || undefined,
    });
  };

  const reset = () => {
    setName('');
    setUrl('');
    setMethod('GET');
    setHeadersStr('');
    setBody('');
    setAuthType('none');
    setAuthValue('');
    setAuthHeaderName('X-API-Key');
    setJsonPath('');
    setSchedule(DEFAULT_SCHEDULE);
    setTestResult(null);
    testFetch.reset();
  };

  const testPassed = testResult?.success === true;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleCreate}>
          <DialogHeader>
            <DialogTitle>HTTP API Connector</DialogTitle>
            <DialogDescription>
              Pull data from a REST API endpoint on a schedule.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Table Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. API Data"
              />
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div className="grid gap-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); clearTest(); }}
                  placeholder="https://api.example.com/data"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Method</Label>
                <Select value={method} onValueChange={(v) => { setMethod(v as 'GET' | 'POST'); clearTest(); }}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Authentication</Label>
              <Select value={authType} onValueChange={(v) => { setAuthType(v as typeof authType); clearTest(); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="bearer">Bearer Token</SelectItem>
                  <SelectItem value="basic">Basic Auth</SelectItem>
                  <SelectItem value="api_key">API Key</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {authType !== 'none' && (
              <div className="grid gap-2">
                <Label htmlFor="authValue">
                  {authType === 'bearer'
                    ? 'Token'
                    : authType === 'basic'
                      ? 'Credentials (base64)'
                      : 'API Key'}
                </Label>
                <Input
                  id="authValue"
                  type="password"
                  value={authValue}
                  onChange={(e) => { setAuthValue(e.target.value); clearTest(); }}
                  placeholder={
                    authType === 'bearer'
                      ? 'Bearer token value'
                      : authType === 'basic'
                        ? 'base64 encoded user:pass'
                        : 'API key value'
                  }
                />
              </div>
            )}

            {authType === 'api_key' && (
              <div className="grid gap-2">
                <Label htmlFor="authHeaderName">Header Name</Label>
                <Input
                  id="authHeaderName"
                  value={authHeaderName}
                  onChange={(e) => { setAuthHeaderName(e.target.value); clearTest(); }}
                  placeholder="X-API-Key"
                />
              </div>
            )}

            {method === 'POST' && (
              <div className="grid gap-2">
                <Label htmlFor="body">Request Body (JSON)</Label>
                <Textarea
                  id="body"
                  value={body}
                  onChange={(e) => { setBody(e.target.value); clearTest(); }}
                  placeholder='{"query": "..."}'
                  className="font-mono text-xs"
                  rows={3}
                />
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="headers">Headers (optional, JSON or key: value per line)</Label>
              <Textarea
                id="headers"
                value={headersStr}
                onChange={(e) => { setHeadersStr(e.target.value); clearTest(); }}
                placeholder="Content-Type: application/json"
                className="font-mono text-xs"
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="jsonPath">Response JSON Path (optional)</Label>
              <Input
                id="jsonPath"
                value={jsonPath}
                onChange={(e) => { setJsonPath(e.target.value); clearTest(); }}
                placeholder="data.results"
              />
              <p className="text-muted-foreground text-xs">
                Dot-notation path to the data array in the response.
              </p>
            </div>

            <div className="grid gap-2">
              <Label>Schedule</Label>
              <SchedulePicker value={schedule} onChange={setSchedule} />
            </div>

            {/* Test result: error */}
            {testResult && !testResult.success && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-700 dark:text-red-300">{testResult.error}</p>
              </div>
            )}

            {/* Test result: success */}
            {testResult?.success && testResult.fields && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950">
                <div className="mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    {testResult.record_count} record{testResult.record_count !== 1 ? 's' : ''} &middot; {testResult.fields.length} field{testResult.fields.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {testResult.fields.map((f) => (
                    <Badge key={f} variant="secondary" className="text-xs">
                      {f}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!url || testFetch.isPending}
              onClick={handleTest}
            >
              {testFetch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Test Connection
            </Button>
            <Button type="submit" disabled={!url || !testPassed || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
